import { useSelector, useDispatch } from 'react-redux'
import {
  setOrders,
  addOrder,
  updateOrder,
  deleteOrder,
  addOrderItem,
  updateOrderItem,
  removeOrderItem,
  selectOrder,
  clearSelection,
  setSearchTerm,
  setStatusFilter,
  setSupplierFilter,
  setDepartmentFilter,
  setDateRange,
  clearFilters,
  updateOrderStatus,
  addApproval,
  updateApproval,
  setLoading,
  setError
} from '../../../../../custom/Inventory Management/redux/purchaseOrderSlice'
import {
  generateId,
  calculateOrderTotal
} from '../../../../../custom/Inventory Management/components/InventoryItems/utils/helpers'
import { useState } from 'react'

const usePurchaseOrders = () => {
  const dispatch = useDispatch()
  const orders = useSelector((state) => state.purchaseOrders.orders)
  const filteredOrders = useSelector((state) => state.purchaseOrders.filteredOrders)
  const selectedOrder = useSelector((state) => state.purchaseOrders.selectedOrder)
  const searchTerm = useSelector((state) => state.purchaseOrders.searchTerm)
  const statusFilter = useSelector((state) => state.purchaseOrders.statusFilter)
  const supplierFilter = useSelector((state) => state.purchaseOrders.supplierFilter)
  const departmentFilter = useSelector((state) => state.purchaseOrders.departmentFilter)
  const dateRange = useSelector((state) => state.purchaseOrders.dateRange)
  const loading = useSelector((state) => state.purchaseOrders.loading)
  const error = useSelector((state) => state.purchaseOrders.error)

  // Local state for modal management
  const [isAddModalVisible, setIsAddModalVisible] = useState(false)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isDeleteModalVisible, setIsDeleteModalVisible] = useState(false)
  const [isViewModalVisible, setIsViewModalVisible] = useState(false)
  const [isApprovalModalVisible, setIsApprovalModalVisible] = useState(false)

  // Modal visibility handlers
  const showAddModal = () => {
    setIsAddModalVisible(true)
  }

  const hideAddModal = () => {
    setIsAddModalVisible(false)
  }

  const showEditModal = (order) => {
    dispatch(selectOrder(order))
    setIsEditModalVisible(true)
  }

  const hideEditModal = () => {
    setIsEditModalVisible(false)
  }

  const showDeleteModal = (order) => {
    dispatch(selectOrder(order))
    setIsDeleteModalVisible(true)
  }

  const hideDeleteModal = () => {
    setIsDeleteModalVisible(false)
  }

  const showViewModal = (order) => {
    dispatch(selectOrder(order))
    setIsViewModalVisible(true)
  }

  const hideViewModal = () => {
    setIsViewModalVisible(false)
  }

  const showApprovalModal = (order) => {
    dispatch(selectOrder(order))
    setIsApprovalModalVisible(true)
  }

  const hideApprovalModal = () => {
    setIsApprovalModalVisible(false)
  }

  // CRUD operations
  const createPurchaseOrder = (order) => {
    try {
      // Generate a new ID if none is provided
      if (!order.id) {
        order.id = generateId(
          'po-',
          orders.map((o) => o.id)
        )
      }

      // Initialize arrays if not provided
      if (!order.itemsOrdered) {
        order.itemsOrdered = []
      }

      if (!order.approvalChain) {
        order.approvalChain = []
      }

      // Set initial status if not provided
      if (!order.status) {
        order.status = 'Draft'
      }

      // Calculate total cost if not provided
      if (!order.totalCost) {
        order.totalCost = calculateOrderTotal(order.itemsOrdered)
      }

      // Set order date to today if not provided
      if (!order.orderDate) {
        order.orderDate = new Date().toISOString().split('T')[0]
      }

      dispatch(addOrder(order))
      hideAddModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error creating purchase order'))
      return false
    }
  }

  const updatePurchaseOrder = (order) => {
    try {
      // Recalculate total cost
      order.totalCost = calculateOrderTotal(order.itemsOrdered)

      dispatch(updateOrder(order))
      hideEditModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error updating purchase order'))
      return false
    }
  }

  const deletePurchaseOrder = (id) => {
    try {
      dispatch(deleteOrder(id))
      hideDeleteModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error deleting purchase order'))
      return false
    }
  }

  // Order item operations
  const addItemToOrder = (orderId, item) => {
    try {
      // Calculate total cost for the item
      item.totalCost = item.quantity * item.unitCost

      dispatch(addOrderItem({ orderId, item }))
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error adding item to order'))
      return false
    }
  }

  const updateOrderItemQuantity = (orderId, itemId, quantity) => {
    try {
      dispatch(
        updateOrderItem({
          orderId,
          itemId,
          updates: { quantity }
        })
      )
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error updating item quantity'))
      return false
    }
  }

  const removeItemFromOrder = (orderId, itemId) => {
    try {
      dispatch(removeOrderItem({ orderId, itemId }))
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error removing item from order'))
      return false
    }
  }

  // Status and approval operations
  const changeOrderStatus = (orderId, status) => {
    try {
      dispatch(updateOrderStatus({ orderId, status }))
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error changing order status'))
      return false
    }
  }

  const addOrderApproval = (orderId, approval) => {
    try {
      // Set timestamp to now if not provided
      if (!approval.timestamp) {
        approval.timestamp = new Date().toISOString()
      }

      dispatch(addApproval({ orderId, approval }))
      hideApprovalModal()
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error adding approval'))
      return false
    }
  }

  const updateOrderApproval = (orderId, approverId, status) => {
    try {
      dispatch(
        updateApproval({
          orderId,
          approverId,
          status,
          timestamp: new Date().toISOString()
        })
      )
      return true
    } catch (error) {
      dispatch(setError(error.message || 'Error updating approval'))
      return false
    }
  }

  // Search and filtering
  const handleSearch = (term) => {
    dispatch(setSearchTerm(term))
  }

  const filterByStatus = (status) => {
    dispatch(setStatusFilter(status))
  }

  const filterBySupplier = (supplierId) => {
    dispatch(setSupplierFilter(supplierId))
  }

  const filterByDepartment = (department) => {
    dispatch(setDepartmentFilter(department))
  }

  const filterByDateRange = (range) => {
    dispatch(setDateRange(range))
  }

  const resetFilters = () => {
    dispatch(clearFilters())
  }

  // Selection handling
  const selectPurchaseOrder = (order) => {
    dispatch(selectOrder(order))
  }

  const clearPurchaseOrderSelection = () => {
    dispatch(clearSelection())
  }

  return {
    // Data
    orders,
    filteredOrders,
    selectedOrder,
    searchTerm,
    statusFilter,
    supplierFilter,
    departmentFilter,
    dateRange,
    loading,
    error,

    // Modal state and handlers
    isAddModalVisible,
    isEditModalVisible,
    isDeleteModalVisible,
    isViewModalVisible,
    isApprovalModalVisible,
    showAddModal,
    hideAddModal,
    showEditModal,
    hideEditModal,
    showDeleteModal,
    hideDeleteModal,
    showViewModal,
    hideViewModal,
    showApprovalModal,
    hideApprovalModal,

    // CRUD operations
    createPurchaseOrder,
    updatePurchaseOrder,
    deletePurchaseOrder,

    // Order item operations
    addItemToOrder,
    updateOrderItemQuantity,
    removeItemFromOrder,

    // Status and approval operations
    changeOrderStatus,
    addOrderApproval,
    updateOrderApproval,

    // Search and filtering
    handleSearch,
    filterByStatus,
    filterBySupplier,
    filterByDepartment,
    filterByDateRange,
    resetFilters,

    // Selection handling
    selectPurchaseOrder,
    clearPurchaseOrderSelection
  }
}

export default usePurchaseOrders
